import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Project } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Overlay";
import { Field, Input, Textarea } from "@/ui/Field";
import { FormError } from "@/features/auth/AuthCard";
import { relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/o/$orgId/projects/")({
  component: ProjectsScreen,
});

function ProjectsScreen() {
  const { orgId } = Route.useParams();
  const [creating, setCreating] = useState(false);

  const projects = useQuery({
    queryKey: ["projects", orgId],
    queryFn: ({ signal }) =>
      api.get<{ projects: Project[] }>(`/organisations/${orgId}/projects`, undefined, signal),
  });

  return (
    <div className="flex flex-col gap-6">
      <Head
        title="Projects"
        description="A project holds the services, databases and domains of one application, across production, staging and development."
        actions={
          <Button tone="primary" onClick={() => setCreating(true)}>
            New project
          </Button>
        }
      />

      <CreateProjectDialog orgId={orgId} open={creating} onOpenChange={setCreating} />

      {projects.isLoading && (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      )}

      {projects.isError && <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />}

      {projects.data && projects.data.projects.length === 0 && (
        <Panel padded={false}>
          {/* No button. "New project" is in the page header and stays there
              once projects exist; two of them on an otherwise empty page is
              one too many. */}
          <EmptyState
            title="No projects yet"
            description="Make one to deploy your first service. You can rename it later; the identifier stays the same."
          />
        </Panel>
      )}

      {projects.data && projects.data.projects.length > 0 && (
        /* A list, not a grid of cards.
           Three cards across is the shape of a marketing page, and with one
           project it is a small box alone in a thousand pixels of nothing.
           A project is a row: a name, what is in it, when it was made, and a
           way in. Rows also stay readable at forty projects, which a grid of
           cards does not — it becomes a wall you have to read all of. */
        <Panel padded={false}>
          <ul>
            {projects.data.projects.map((project) => (
              <li key={project.id} className="border-b border-[var(--border)] last:border-b-0">
                <Link
                  to="/o/$orgId/projects/$projectId"
                  params={{ orgId, projectId: project.id }}
                  className="row-live flex items-center gap-4 px-5 py-3.5"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="t-strong truncate">{project.display_name}</span>
                      <span className="mono shrink-0 text-[var(--text-muted)]">
                        {project.slug}
                      </span>
                    </span>
                    {project.description && (
                      <span className="t-meta mt-0.5 block truncate">{project.description}</span>
                    )}
                  </span>

                  <span className="hidden shrink-0 items-center gap-x-4 sm:flex">
                    <Inventory count={project.services} one="service" many="services" />
                    <Inventory count={project.databases} one="database" many="databases" />
                    <Inventory count={project.domains} one="domain" many="domains" />
                  </span>

                  <span className="t-meta hidden w-24 shrink-0 text-right tabular-nums lg:block">
                    {relativeTime(project.created_at)}
                  </span>

                  <ChevronIcon />
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}

/** The chevron that says a row opens something. */
function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className="shrink-0 text-[var(--text-muted)]"
    >
      <path
        d="M6 3.5L10.5 8L6 12.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * One count on a project row.
 *
 * A zero is drawn in the tertiary colour rather than left out. Omitting it
 * makes two cards with different contents look like two cards with the same
 * contents, and "no databases" is a fact somebody choosing a project wants —
 * it is not the absence of one.
 */
function Inventory({ count, one, many }: { count: number; one: string; many: string }) {
  return (
    <span className={count > 0 ? "t-meta text-[var(--text-secondary)]" : "t-meta"}>
      <span className="tabular font-medium">{count}</span> {count === 1 ? one : many}
    </span>
  );
}

function CreateProjectDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.post<{ project: Project }>(`/organisations/${orgId}/projects`, {
        display_name: displayName,
        description,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["projects", orgId] });
      onOpenChange(false);
      setDisplayName("");
      setDescription("");
      await navigate({
        to: "/o/$orgId/projects/$projectId",
        params: { orgId, projectId: result.project.id },
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New project"
      description="Production, staging and development environments are created with it."
      footer={
        <>
          <Button tone="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            tone="primary"
            busy={create.isPending}
            disabled={displayName.trim().length < 2}
            onClick={() => create.mutate()}
          >
            Create project
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Name" required hint="Usually the name of the application.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              autoFocus
              required
              minLength={2}
              maxLength={60}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Description" hint="Optional. What this application does.">
          {({ id, describedBy }) => (
            <Textarea
              id={id}
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              aria-describedby={describedBy}
              className="font-sans text-sm"
            />
          )}
        </Field>

        {create.error ? <FormError error={create.error} /> : null}
      </form>
    </Dialog>
  );
}
